export type Language = 'de' | 'en';

export interface Translations {
  [key: string]: {
    de: string;
    en: string;
  };
}

export const UI_TRANSLATIONS: Translations = {
  // Brand & Slogan
  tagline: {
    de: 'Deals & Gutscheine',
    en: 'Deals & Coupons'
  },
  sloganPrefix: {
    de: 'Ihr Ziel für',
    en: 'Your destination for'
  },
  sloganHighlight1: {
    de: 'erstklassige',
    en: 'premium'
  },
  sloganHighlight2: {
    de: 'handverlesene',
    en: 'handpicked'
  },
  sloganHighlight3: {
    de: 'Sparerlebnis',
    en: 'savings experience'
  },
  sloganRest: {
    de: 'Gutscheine und Rabatte, um Ihnen das beste',
    en: 'vouchers and discounts to give you the best'
  },
  sloganSuffix: {
    de: 'zu bieten!',
    en: 'ever!'
  },

  // Banner
  topBannerBadge: {
    de: 'Neu',
    en: 'New'
  },
  topBannerText: {
    de: 'Exklusive Deals: Jetzt bis zu 85.000 Membership Rewards® Punkte bei American Express sichern!',
    en: 'Exclusive Deals: Get up to 85,000 Membership Rewards® points with American Express now!'
  },

  // Navbar
  navAllVouchers: {
    de: 'Alle Gutscheine',
    en: 'All Vouchers'
  },
  navCategories: {
    de: 'Kategorien',
    en: 'Categories'
  },
  navTopDeal: {
    de: 'Top Deal: Amex',
    en: 'Top Deal: Amex'
  },

  // Hero Section
  heroBadge: {
    de: 'Geprüfte Deals & Gutscheine 2026',
    en: 'Verified Deals & Promo Codes 2026'
  },
  heroTitle1: {
    de: 'Sparen war noch nie',
    en: 'Saving has never been'
  },
  heroTitle2: {
    de: 'so einfach.',
    en: 'so easy.'
  },
  heroSubtitle: {
    de: 'Entdecke handverlesene Rabattcodes, Willkommensprämien und exklusive Einladungslinks für führende Marken.',
    en: 'Discover handpicked promo codes, welcome bonuses, and exclusive referral links for top brands.'
  },
  searchPlaceholder: {
    de: 'Shop Name (z.B. Uber Eats, N26, Grover...)',
    en: 'Shop Name (e.g., Uber Eats, N26, Grover...)'
  },
  clearSearch: {
    de: 'Suche löschen',
    en: 'Clear search'
  },
  featuredBadge: {
    de: 'Top Deal',
    en: 'Top Deal'
  },

  // Filters & Counts
  allCategories: {
    de: 'Alle',
    en: 'All'
  },
  activeVouchersCount: {
    de: 'Aktive Gutscheine',
    en: 'Active Vouchers'
  },
  verifiedBrandsCount: {
    de: 'Geprüfte Marken',
    en: 'Verified Brands'
  },
  freeBonusCount: {
    de: 'Kostenlos & Sicher',
    en: 'Free & Verified'
  },
  noResultsFound: {
    de: 'Keine passenden Angebote gefunden',
    en: 'No matching offers found'
  },
  noResultsHint: {
    de: 'Versuche einen anderen Suchbegriff oder setze die Filter zurück.',
    en: 'Try another search term or reset filters.'
  },
  resetSearch: {
    de: 'Suche zurücksetzen',
    en: 'Reset Search'
  },

  // Detail Page
  backToOverview: {
    de: 'Zurück zur Übersicht',
    en: 'Back to Overview'
  },
  voucherCode: {
    de: 'Gutscheincode',
    en: 'Voucher Code'
  },
  copyCode: {
    de: 'Kopieren',
    en: 'Copy'
  },
  copied: {
    de: 'Kopiert!',
    en: 'Copied!'
  },
  copiedSuccess: {
    de: 'Erfolgreich in die Zwischenablage kopiert!',
    en: 'Successfully copied to clipboard!'
  },
  linkHint: {
    de: 'Kein Gutscheincode nötig – der Rabatt wird über den Einladungslink automatisch aktiviert!',
    en: 'No promo code required – discount is automatically applied via the referral link!'
  },
  openOffer: {
    de: 'Go (Angebot öffnen)',
    en: 'Go (Open Offer)'
  },
  copyLink: {
    de: 'Link kopieren',
    en: 'Copy Link'
  },
  dealDetails: {
    de: 'Details zum Angebot',
    en: 'Deal Details'
  },
  howToRedeem: {
    de: 'So löst du den Vorteil ein:',
    en: 'How to redeem this offer:'
  },
  step1Code: {
    de: 'Kopiere den Gutscheincode oben.',
    en: 'Copy the voucher code above.'
  },
  step1Link: {
    de: 'Klicke auf "Go (Angebot öffnen)", um zur Partnerseite zu gelangen.',
    en: 'Click "Go (Open Offer)" to visit the partner page.'
  },
  step2Text: {
    de: 'Registriere dich bei oder lege deine Wunschartikel in den Warenkorb.',
    en: 'Sign up or add your desired items to the shopping cart.'
  },
  step3Code: {
    de: 'Füge den Code im Gutscheinfeld ein. Viel Spaß beim Sparen!',
    en: 'Paste the code in the promo box at checkout. Enjoy your savings!'
  },
  step3Link: {
    de: 'Dein Vorteil wird automatisch berücksichtigt. Viel Spaß beim Sparen!',
    en: 'Your discount is automatically applied. Enjoy your savings!'
  },
  shareWithFriends: {
    de: 'Diesen Deal mit Freunden teilen:',
    en: 'Share this deal with friends:'
  },
  relatedDeals: {
    de: 'Weitere Top-Gutscheine entdecken',
    en: 'Discover More Top Deals'
  },
  notFoundTitle: {
    de: 'Gutschein nicht gefunden',
    en: 'Voucher Not Found'
  },
  notFoundSubtitle: {
    de: 'Das gesuchte Angebot existiert leider nicht oder ist abgelaufen.',
    en: 'The requested deal does not exist or has expired.'
  },

  // FAQ
  faqTitle: {
    de: 'Häufig gestellte Fragen (FAQ)',
    en: 'Frequently Asked Questions (FAQ)'
  },
  faqSubtitle: {
    de: 'Alles was du über Gutscheine, Rabattcodes und Prämien auf Coupon Showcase wissen musst.',
    en: 'Everything you need to know about vouchers, promo codes, and rewards on Coupon Showcase.'
  }
};
